<template>
  <div class="media-capture-uploader">
    <div class="media-buttons mb-2 d-flex justify-content-center gap-3">
      <!-- Botón para imagen -->
      <label class="media-btn" title="Capturar imagen">
        <i class="bi bi-camera-fill"></i>
        <input
          type="file"
          accept="image/*"
          capture="environment"
          @change="handleMediaCapture"
          style="display: none"
        />
      </label>

      <!-- Botón para video -->
      <label class="media-btn" title="Capturar video">
        <i class="bi bi-camera-video-fill"></i>
        <input
          type="file"
          accept="video/*"
          capture="environment"
          @change="handleMediaCapture"
          style="display: none"
        />
      </label>
      <!-- Botón para audio -->
      <label class="media-btn" title="Capturar audio" @click="onAudioButtonClick">
        <i class="bi bi-mic-fill"></i>
        <input
          v-if="!isIOS"
          type="file"
          accept="audio/*"
          capture
          @change="handleMediaCapture"
          style="display: none"
        />
      </label>
      <!-- Modal grabadora para iOS -->
      <div v-if="showAudioRecorder" class="audio-modal">
        <div class="audio-modal-content">
          <p>{{ isRecording ? 'Grabando...' : 'Listo para grabar' }}</p>
          <button @click="isRecording ? stopRecording() : startRecording()" class="btn btn-primary btn-sm">
            {{ isRecording ? 'Detener' : 'Grabar' }}
          </button>
          <button v-if="audioBlob" @click="saveAudio" class="btn btn-success btn-sm">Guardar</button>
          <button @click="closeAudioRecorder" class="btn btn-secondary btn-sm">Cerrar</button>
          <audio v-if="audioUrl" :src="audioUrl" controls style="width:100%;margin-top:8px;" />
        </div>
      </div>
    </div>
    <button v-if="showUploadButton" :disabled="files.length === 0" @click="uploadAllFiles" class="btn btn-secondary btn-sm">
      Subir Archivo(s)
      </button>
      <div v-if="files.length > 0" class="files-list">
        <div v-for="(fileItem, idx) in files" :key="idx">
        {{ fileItem.file.name }}
        <span v-if="fileItem.status === 'uploading'">Subiendo...</span>
        <span v-else-if="fileItem.status === 'success'">Subido</span>
        <span v-else-if="fileItem.status === 'error'">Error</span>
        </div>
      </div>
    </div>

</template>

<script setup lang="ts">
import { ref } from 'vue';
// Detectar iOS
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

// Estado para grabadora
const showAudioRecorder = ref(false);
const isRecording = ref(false);

let mediaRecorder: MediaRecorder | null = null;
let audioChunks: BlobPart[] = [];
const audioBlob = ref<Blob|null>(null);
const audioUrl = ref<string|null>(null);
// Tipos soportados
function getSupportedAudioType() {
  if (isIOS && MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  if (MediaRecorder.isTypeSupported('audio/ogg')) return 'audio/ogg';
  return '';
}

function onAudioButtonClick(e: Event) {
  if (isIOS) {
    e.preventDefault();
    showAudioRecorder.value = true;
  }
}

function closeAudioRecorder() {
  showAudioRecorder.value = false;
  isRecording.value = false;
  audioBlob.value = null;
  audioUrl.value = null;
}


async function startRecording() {
  audioBlob.value = null;
  audioUrl.value = null;
  audioChunks = [];
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mimeType = getSupportedAudioType();
    mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) audioChunks.push(e.data);
    };
    mediaRecorder.onstop = () => {
      const type = mimeType || 'audio/webm';
      const blob = new Blob(audioChunks, { type });
      audioBlob.value = blob;
      audioUrl.value = URL.createObjectURL(blob);
    };
    mediaRecorder.start();
    isRecording.value = true;
  } catch (err) {
    alert('No se pudo acceder al micrófono.');
    isRecording.value = false;
  }
}

function stopRecording() {
  if (mediaRecorder && isRecording.value) {
    mediaRecorder.stop();
    isRecording.value = false;
  }
}

function saveAudio() {
  if (audioBlob.value) {
    // Detectar tipo y extensión
    let type = audioBlob.value.type;
    let ext = 'webm';
    if (type === 'audio/mp4') ext = 'mp4';
    else if (type === 'audio/ogg') ext = 'ogg';
    else if (type === 'audio/wav') ext = 'wav';
    const file = new File([audioBlob.value], `audio_${Date.now()}.${ext}`, { type });
    files.value.push({ file, status: 'pending' });
    closeAudioRecorder();
  }
}
import API from '../services';

interface FileItem {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  response?: any;
  error?: any;
}
const props = defineProps<{
  showUploadButton?: boolean;
  destPath?: string;
}>();

const files = ref<FileItem[]>([]);

function handleMediaCapture(event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    // Agrega todos los archivos seleccionados
    const newFiles = Array.from(target.files).map(file => ({
      file,
      status: 'pending' as const
    }));
    files.value.push(...newFiles);
  }
}

async function uploadSingleFile(fileItem: FileItem): Promise<{ success: boolean; response?: any; error?: any }> {
  if (!fileItem.file) return { success: false };

  fileItem.status = 'uploading';

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const base64Content = (reader.result as string).split(',')[1];
      const payload = {
        filename: fileItem.file.name,
        contentType: fileItem.file.type,
        fileContent: base64Content,
        destPath: props.destPath || ''
      };

      try {
        const response = await API.uploadFileService(payload);
        fileItem.status = 'success';
        fileItem.response = response;
        resolve({ success: true, response });
      } catch (error) {
        fileItem.status = 'error';
        fileItem.error = error;
        resolve({ success: false, error });
      }
    };
    reader.readAsDataURL(fileItem.file);
  });
}

async function uploadAllFiles() {
  const pendingFiles = files.value.filter(f => f.status === 'pending');
  const results: { file: File; success: boolean; response?: any; error?: any }[] = [];

  for (const fileItem of pendingFiles) {
    const result = await uploadSingleFile(fileItem);
    results.push({
      file: fileItem.file,
      ...result
    });
  }
  return results;
}

defineExpose({
  uploadAllFiles,
  files
});
</script>

<style scoped>
.audio-modal {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.audio-modal-content {
  background: #fff;
  padding: 24px 16px;
  border-radius: 12px;
  min-width: 260px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>

<style scoped>
.media-capture-uploader {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.btn-group label {
  cursor: pointer;
  margin-bottom: 0;
}
.files-list {
  margin-top: 10px;
  font-size: 13px;
}
.media-buttons {
  margin-bottom: 16px;
}

.media-btn {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #1976d2;
  color: #fff;
  border: none;
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  transition: background 0.2s;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.media-btn:active {
  background: #1565c0;
}

.media-btn i {
  pointer-events: none;
}

.media-btn input[type="file"] {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
</style>